import type { InboxPullRequest } from '@/app/(home)/inbox/types';

import type { PullRequestIdentity } from './patch-inbox-pull-request';

type PendingEntry = {
  snapshot: InboxPullRequest;
  expiresAt: number;
};

type Store = Record<string, PendingEntry>;

const STORAGE_KEY = 'mrge-pending-optimistic-mutations';
const TTL_MS = 60_000;

function buildKey(identity: PullRequestIdentity): string {
  return `${identity.owner.toLowerCase()}/${identity.repo.toLowerCase()}#${identity.number}`;
}

function isValidEntry(value: unknown): value is PendingEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    Number.isFinite(record.expiresAt) &&
    typeof record.snapshot === 'object' &&
    record.snapshot !== null
  );
}

function readStore(): Store {
  if (typeof window === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }
    const validated: Store = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (isValidEntry(value)) {
        validated[key] = value;
      }
    }
    return validated;
  } catch {
    return {};
  }
}

function writeStore(store: Store): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const keys = Object.keys(store);
    if (keys.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
  } catch {
    // localStorage full or unavailable
  }
}

export function setPendingOptimisticMutation(
  identity: PullRequestIdentity,
  snapshot: InboxPullRequest,
): void {
  const key = buildKey(identity);
  const store = readStore();
  store[key] = { snapshot, expiresAt: Date.now() + TTL_MS };
  writeStore(store);
}

export function getPendingOptimisticSnapshot(
  identity: PullRequestIdentity,
): InboxPullRequest | null {
  const key = buildKey(identity);
  const store = readStore();
  const entry = store[key];
  if (!entry) {
    return null;
  }
  if (Date.now() > entry.expiresAt) {
    delete store[key];
    writeStore(store);
    return null;
  }
  return entry.snapshot;
}

function requestedReviewerLogins(pr: InboxPullRequest): string[] {
  return (pr.requested_reviewers ?? []).map((reviewer) => reviewer.login.toLowerCase()).sort();
}

function haveSameRequestedReviewers(
  serverPr: InboxPullRequest,
  snapshot: InboxPullRequest,
): boolean {
  const serverReviewers = requestedReviewerLogins(serverPr);
  const snapshotReviewers = requestedReviewerLogins(snapshot);

  if (serverReviewers.length !== snapshotReviewers.length) {
    return false;
  }

  return serverReviewers.every((login, index) => login === snapshotReviewers[index]);
}

function haveSameLabels(serverPr: InboxPullRequest, snapshot: InboxPullRequest): boolean {
  const serverLabels = (serverPr.labels ?? []).map((label) => label.name.toLowerCase()).sort();
  const snapshotLabels = (snapshot.labels ?? []).map((label) => label.name.toLowerCase()).sort();

  return (
    serverLabels.length === snapshotLabels.length &&
    serverLabels.every((name, index) => name === snapshotLabels[index])
  );
}

export function isOptimisticSnapshotConfirmedByServer(
  serverPr: InboxPullRequest,
  snapshot: InboxPullRequest,
): boolean {
  // Terminal server states are authoritative: once the server reports the PR as closed or merged,
  // the optimistic snapshot (which still shows it as open) must not keep blocking the update, or
  // the PR lingers in the open inbox until the snapshot TTL expires. Treat it as confirmed so the
  // server's closed/merged truth wins immediately.
  if (serverPr.state !== 'open' || serverPr.mergedAt) {
    return true;
  }

  return (
    serverPr.reviewState === snapshot.reviewState &&
    haveSameRequestedReviewers(serverPr, snapshot) &&
    haveSameLabels(serverPr, snapshot) &&
    serverPr.title === snapshot.title &&
    serverPr.draft === snapshot.draft &&
    serverPr.state === snapshot.state &&
    serverPr.mergedAt === snapshot.mergedAt
  );
}

/**
 * Reads localStorage once and returns all non-expired snapshots keyed by
 * "owner/repo#number". Expired entries are pruned in a single write.
 */
export function getActiveOptimisticSnapshots(): Map<string, InboxPullRequest> {
  const store = readStore();
  const now = Date.now();
  const result = new Map<string, InboxPullRequest>();
  let pruned = false;

  for (const [key, entry] of Object.entries(store)) {
    if (now > entry.expiresAt) {
      delete store[key];
      pruned = true;
    } else {
      result.set(key, entry.snapshot);
    }
  }

  if (pruned) {
    writeStore(store);
  }

  return result;
}

export function clearPendingOptimisticMutation(identity: PullRequestIdentity): void {
  const key = buildKey(identity);
  const store = readStore();
  if (store[key]) {
    delete store[key];
    writeStore(store);
  }
}
